import { useEffect, useMemo, useRef, useState, type ChangeEvent } from "react";
import { Icon } from "../../components/Icon";
import { PuzzlePlayer } from "../../components/puzzles/PuzzlePlayer";
import {
  Button,
  Card,
  EmptyState,
  Pill,
  SectionHeading,
} from "../../components/ui";
import { WorldMapCanvas } from "../../components/WorldMapCanvas";
import { GUILDS } from "../../data/seed";
import {
  generatePuzzleInstance,
  validatePuzzleSubmission,
} from "../../puzzles/core/registry";
import {
  createTimeSeed,
  vectorDroneDifficultyLabels,
  type VectorDroneDifficulty,
} from "../../puzzles/core/vectorDrone";
import {
  puzzlePackageSchema,
  type PuzzlePackageDefinition,
} from "../../puzzles/puzzlePackage";
import { usePortal } from "../../state/PortalContext";
import type { Curriculum, MapConnection, Quest, Region } from "../../types/domain";

const configFields: Record<
  Quest["templateId"],
  Array<{ key: string; label: string; step?: number }>
> = {
  "vector-drone": [
    { key: "windXMin", label: "Wind X minimum" },
    { key: "windXMax", label: "Wind X maximum" },
    { key: "windYMin", label: "Wind Y minimum" },
    { key: "windYMax", label: "Wind Y maximum" },
    { key: "targetXMin", label: "Target X minimum" },
    { key: "targetXMax", label: "Target X maximum" },
    { key: "targetYMin", label: "Target Y minimum" },
    { key: "targetYMax", label: "Target Y maximum" },
    { key: "inputMin", label: "Input minimum" },
    { key: "inputMax", label: "Input maximum" },
    { key: "tolerance", label: "Landing tolerance", step: 0.5 },
  ],
  "trig-cannon": [
    { key: "gravity", label: "Gravity", step: 0.1 },
    { key: "targetXMin", label: "Target X minimum" },
    { key: "targetXMax", label: "Target X maximum" },
    { key: "targetYMin", label: "Target Y minimum" },
    { key: "targetYMax", label: "Target Y maximum" },
    { key: "angleMin", label: "Angle minimum" },
    { key: "angleMax", label: "Angle maximum" },
    { key: "powerMin", label: "Power minimum" },
    { key: "powerMax", label: "Power maximum" },
    { key: "tolerance", label: "Hit tolerance", step: 0.5 },
  ],
  "platformer-jump": [
    { key: "gravityMin", label: "Gravity minimum", step: 0.1 },
    { key: "gravityMax", label: "Gravity maximum", step: 0.1 },
    { key: "velocityMin", label: "Velocity minimum", step: 0.1 },
    { key: "velocityMax", label: "Velocity maximum", step: 0.1 },
    { key: "obstacleDistance", label: "Obstacle distance", step: 0.1 },
    { key: "obstacleHeight", label: "Obstacle height", step: 0.1 },
    { key: "landingMin", label: "Landing start", step: 0.1 },
    { key: "landingMax", label: "Landing end", step: 0.1 },
    { key: "horizontalSpeed", label: "Horizontal speed", step: 0.1 },
    { key: "simulationStep", label: "Simulation step", step: 0.001 },
  ],
  "coordinate-maze": [
    { key: "gridSize", label: "Grid size" },
    { key: "minPathLength", label: "Minimum path length" },
  ],
  "binary-beacon": [
    { key: "minValue", label: "Minimum value" },
    { key: "maxValue", label: "Maximum value" },
  ],
  "vector-cannon": [
    { key: "targetXMin", label: "Target X minimum" },
    { key: "targetXMax", label: "Target X maximum" },
    { key: "targetYMin", label: "Target Y minimum" },
    { key: "targetYMax", label: "Target Y maximum" },
    { key: "windXMin", label: "Wind X minimum" },
    { key: "windXMax", label: "Wind X maximum" },
    { key: "windYMin", label: "Wind Y minimum" },
    { key: "windYMax", label: "Wind Y maximum" },
    { key: "tolerance", label: "Tolerance", step: 0.1 },
  ],
  "spawn-point-builder": [
    { key: "gridSize", label: "Grid size" },
    { key: "tolerance", label: "Tolerance", step: 0.1 },
  ],
  "pathfinding-trail": [
    { key: "segments", label: "Segment count" },
    { key: "tolerance", label: "Tolerance", step: 0.1 },
  ],
  "quest-equation": [
    { key: "coefficientMin", label: "Coefficient minimum" },
    { key: "coefficientMax", label: "Coefficient maximum" },
    { key: "solutionMin", label: "Solution minimum" },
    { key: "solutionMax", label: "Solution maximum" },
  ],
  "turret-aim": [{ key: "toleranceDegrees", label: "Tolerance degrees", step: 0.1 }],
  "transformation-machine": [{ key: "tolerance", label: "Tolerance", step: 0.01 }],
  "force-arena": [{ key: "tolerance", label: "Tolerance", step: 0.1 }],
  "collision-zone": [{ key: "tolerance", label: "Tolerance", step: 0.01 }],
  "xp-counter-overflow": [{ key: "bits", label: "Bit width" }],
  "sat-inspector": [],
  "coming-soon": [],
};

const templateOptions: Array<{ id: Quest["templateId"]; label: string }> = [
  { id: "vector-drone", label: "Vector Drone" },
  { id: "trig-cannon", label: "Trigonometry Cannon" },
  { id: "platformer-jump", label: "Platformer Jump" },
  { id: "coordinate-maze", label: "Coordinate Maze" },
  { id: "binary-beacon", label: "Binary Beacon" },
  { id: "vector-cannon", label: "Vector Cannon" },
  { id: "spawn-point-builder", label: "Spawn Point Builder" },
  { id: "pathfinding-trail", label: "Pathfinding Trail" },
  { id: "quest-equation", label: "Quest Equation" },
  { id: "turret-aim", label: "Turret Aim" },
  { id: "transformation-machine", label: "Transformation Machine" },
  { id: "force-arena", label: "Force Arena" },
  { id: "collision-zone", label: "Collision Zone" },
  { id: "xp-counter-overflow", label: "XP Counter Overflow" },
  { id: "sat-inspector", label: "SAT Inspector" },
  { id: "coming-soon", label: "Coming Soon" },
];

export function FacultyCurriculumPage() {
  const {
    cohort,
    curriculum,
    progress,
    saveCurriculum,
    publishCurriculum,
    updateRegionUnlocks,
    notify,
  } = usePortal();
  const [draft, setDraft] = useState<Curriculum>(curriculum);
  const [guild, setGuild] = useState(GUILDS[0].id);
  const [selectedRegionId, setSelectedRegionId] = useState(
    curriculum.regions[0].id,
  );
  const [selectedQuestId, setSelectedQuestId] = useState(
    curriculum.quests[0].id,
  );
  const [selectedConnectionId, setSelectedConnectionId] = useState<string | null>(
    null,
  );
  const [routeTargetId, setRouteTargetId] = useState(
    curriculum.regions[1]?.id ?? curriculum.regions[0].id,
  );
  const [preview, setPreview] = useState(false);
  const [previewSeed, setPreviewSeed] = useState(() => createTimeSeed());
  const [previewResult, setPreviewResult] = useState<{
    success: boolean;
    score: number;
    feedback: string;
  } | null>(null);
  const [publishing, setPublishing] = useState(false);
  const [savingUnlocks, setSavingUnlocks] = useState(false);
  const [savingMapLayout, setSavingMapLayout] = useState(false);
  const packageInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => setDraft(curriculum), [curriculum]);
  const selectedRegion = draft.regions.find(
    (region) => region.id === selectedRegionId,
  )!;
  const regionQuests = draft.quests.filter(
    (quest) => quest.regionId === selectedRegionId,
  );
  const selectedQuest =
    draft.quests.find((quest) => quest.id === selectedQuestId) ?? regionQuests[0];
  const selectedGuild = GUILDS.find((entry) => entry.id === guild)!;
  const guildConnections = draft.connections.filter(
    (connection) => connection.guild === guild,
  );
  const routePairConnection =
    guildConnections.find(
      (connection) =>
        (connection.fromRegionId === selectedRegionId &&
          connection.toRegionId === routeTargetId) ||
        (connection.fromRegionId === routeTargetId &&
          connection.toRegionId === selectedRegionId),
    ) ?? null;
  const selectedConnection =
    guildConnections.find((connection) => connection.id === selectedConnectionId) ??
    guildConnections[0] ??
    null;
  const previewInstance = useMemo(
    () =>
      selectedQuest?.templateId !== "coming-soon"
        ? generatePuzzleInstance(
            selectedQuest.templateId,
            selectedQuest.config,
            previewSeed,
          )
        : null,
    [selectedQuest, previewSeed],
  );

  useEffect(() => {
    setPreviewResult(null);
  }, [selectedQuest?.id, previewSeed]);

  const updateRegion = (patch: Partial<Region>) => {
    setDraft((current) => ({
      ...current,
      status: "draft",
      regions: current.regions.map((region) =>
        region.id === selectedRegionId ? { ...region, ...patch } : region,
      ),
    }));
  };

  const moveRegion = (regionId: string, x: number, y: number) => {
    setDraft((current) => ({
      ...current,
      status: "draft",
      regions: current.regions.map((region) =>
        region.id === regionId ? { ...region, x, y } : region,
      ),
    }));
  };

  const updateRegionUnlockDraft = (patch: Partial<Region>) => {
    setDraft((current) => ({
      ...current,
      regions: current.regions.map((region) =>
        region.id === selectedRegionId ? { ...region, ...patch } : region,
      ),
    }));
  };

  const toggleUnlockPrerequisite = (regionId: string) => {
    const current =
      selectedRegion.unlockPrerequisiteRegionIds ??
      selectedRegion.prerequisiteRegionIds;
    const next = current.includes(regionId)
      ? current.filter((id) => id !== regionId)
      : [...current, regionId];
    updateRegionUnlockDraft({ unlockPrerequisiteRegionIds: next });
  };

  const saveUnlockRules = async () => {
    setSavingUnlocks(true);
    try {
      await updateRegionUnlocks(selectedRegion.id, {
        unlockAvailableFrom: selectedRegion.unlockAvailableFrom ?? null,
        unlockCompletionThreshold: selectedRegion.unlockCompletionThreshold ?? 70,
        unlockPrerequisiteRegionIds:
          selectedRegion.unlockPrerequisiteRegionIds ??
          selectedRegion.prerequisiteRegionIds,
      });
    } finally {
      setSavingUnlocks(false);
    }
  };

  const saveMapLayout = async () => {
    setSavingMapLayout(true);
    try {
      await saveCurriculum(draft);
    } catch (error) {
      notify(
        "Save failed",
        error instanceof Error ? error.message : "Unable to save map layout.",
        "warning",
      );
    } finally {
      setSavingMapLayout(false);
    }
  };

  const saveDraft = async () => {
    try {
      await saveCurriculum(draft);
    } catch (error) {
      notify(
        "Save failed",
        error instanceof Error ? error.message : "Unable to save curriculum.",
        "warning",
      );
    }
  };

  const updateConnection = (
    connectionId: string,
    patch: Partial<MapConnection>,
  ) => {
    setDraft((current) => ({
      ...current,
      status: "draft",
      connections: current.connections.map((connection) =>
        connection.id === connectionId ? { ...connection, ...patch } : connection,
      ),
    }));
  };

  const addConnection = () => {
    if (selectedRegionId === routeTargetId) return;
    const from = draft.regions.find((region) => region.id === selectedRegionId);
    const to = draft.regions.find((region) => region.id === routeTargetId);
    if (!from || !to) return;
    if (routePairConnection) {
      setSelectedConnectionId(routePairConnection.id);
      return;
    }
    const connection: MapConnection = {
      id: `${guild}-${crypto.randomUUID()}`,
      guild,
      fromRegionId: selectedRegionId,
      toRegionId: routeTargetId,
      controlX: Number(((from.x + to.x) / 2).toFixed(2)),
      controlY: Number((((from.y + to.y) / 2) - 8).toFixed(2)),
    };
    setDraft((current) => ({
      ...current,
      status: "draft",
      connections: [...current.connections, connection],
    }));
    setSelectedConnectionId(connection.id);
  };

  const deleteConnection = (connectionId: string) => {
    setDraft((current) => ({
      ...current,
      status: "draft",
      connections: current.connections.filter(
        (connection) => connection.id !== connectionId,
      ),
    }));
    setSelectedConnectionId(null);
  };

  const deleteRoutePair = () => {
    if (!routePairConnection) return;
    deleteConnection(routePairConnection.id);
  };

  const clearGuildPath = () => {
    setDraft((current) => ({
      ...current,
      status: "draft",
      connections: current.connections.filter(
        (connection) => connection.guild !== guild,
      ),
    }));
    setSelectedConnectionId(null);
  };

  const updateQuest = (patch: Partial<Quest>) => {
    if (!selectedQuest) return;
    setDraft((current) => ({
      ...current,
      status: "draft",
      quests: current.quests.map((quest) =>
        quest.id === selectedQuest.id ? { ...quest, ...patch } : quest,
      ),
    }));
  };

  const addQuest = () => {
    const subArea = selectedRegion.subAreas[0];
    const quest: Quest = {
      id: `quest-${crypto.randomUUID()}`,
      regionId: selectedRegion.id,
      subAreaId: subArea.id,
      title: "Untitled Interactive Quest",
      storyHook: "A new mathematical disturbance has appeared.",
      learningGoal: subArea.summary,
      practice: "Introduce the mechanic with a guided observation.",
      reflectionPrompt: "What relationship did you observe?",
      reflectionConfig: {
        mode: "text",
        prompt: "What relationship did you observe?",
        minLength: 10,
      },
      templateId: "vector-drone",
      templateVersion: 1,
      config: {
        difficulty: "practice",
        windXMin: 20,
        windXMax: 50,
        windYMin: -20,
        windYMax: 20,
        targetXMin: 80,
        targetXMax: 140,
        targetYMin: 30,
        targetYMax: 100,
        inputMin: -120,
        inputMax: 160,
        tolerance: 8,
      },
      xp: 300,
      gradePoints: 10,
      xpDecay: 0.2,
      gradeDecay: 0.1,
      cooldownMinutes: 0,
      maxAttempts: null,
      competencyEvidence: {
        [`${subArea.id}-competency`]: 25,
      },
      boss: false,
      status: "draft",
      version: 1,
    };
    setDraft((current) => ({ ...current, status: "draft", quests: [...current.quests, quest] }));
    setSelectedQuestId(quest.id);
  };

  const importPuzzlePackage = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    try {
      const parsed = JSON.parse(await file.text());
      const puzzlePackage = puzzlePackageSchema.parse(parsed);
      const quest = createQuestFromPackage(puzzlePackage, selectedRegion);
      setDraft((current) => ({
        ...current,
        status: "draft",
        quests: [...current.quests, quest],
      }));
      setSelectedQuestId(quest.id);
      notify(
        "Puzzle package imported",
        `${puzzlePackage.title} is ready as a draft quest.`,
        "info",
      );
    } catch (error) {
      notify(
        "Import failed",
        error instanceof Error ? error.message : "The package is not valid JSON.",
        "warning",
      );
    }
  };

  const publish = async () => {
    setPublishing(true);
    try {
      await publishCurriculum(draft);
    } finally {
      setPublishing(false);
    }
  };

  return (
    <div className="space-y-7">
      <SectionHeading
        eyebrow="Curriculum Forge"
        title="Design the adventure world"
        detail="Edit map regions and configure registered puzzle templates without writing code. Saves remain drafts until explicitly published."
        action={
          <div className="flex flex-wrap gap-2">
            <Pill tone={draft.status === "draft" ? "amber" : "emerald"}>
              {draft.status} · v{draft.version}
            </Pill>
            <Button variant="secondary" onClick={() => void saveDraft()}>
              <Icon name="Save" size={17} />
              Save draft
            </Button>
            <Button variant="gold" onClick={() => void publish()} disabled={publishing}>
              <Icon name="Rocket" size={17} />
              {publishing ? "Publishing..." : "Publish version"}
            </Button>
          </div>
        }
      />

      <div className="flex max-w-full gap-1 overflow-x-auto rounded-2xl border border-slate-800 bg-slate-950/55 p-1.5">
        {GUILDS.map((entry) => (
          <button
            key={entry.id}
            onClick={() => setGuild(entry.id)}
            className={`min-h-10 whitespace-nowrap rounded-xl px-3 text-xs font-bold ${
              guild === entry.id
                ? "bg-indigo-500 text-white"
                : "text-slate-500 hover:bg-slate-800 hover:text-white"
            }`}
          >
            {entry.name}
          </button>
        ))}
      </div>

      <WorldMapCanvas
        curriculum={draft}
        progress={progress}
        guild={guild}
        selectedRegionId={selectedRegionId}
        selectedConnectionId={selectedConnection?.id}
        onSelect={(region) => {
          setSelectedRegionId(region.id);
          if (routeTargetId === region.id) {
            const nextTarget = draft.regions.find(
              (candidate) => candidate.id !== region.id,
            );
            if (nextTarget) setRouteTargetId(nextTarget.id);
          }
          const firstQuest = draft.quests.find(
            (quest) => quest.regionId === region.id,
          );
          if (firstQuest) setSelectedQuestId(firstQuest.id);
        }}
        onMoveRegion={moveRegion}
        onMoveConnectionControl={(connectionId, controlX, controlY) =>
          updateConnection(connectionId, { controlX, controlY })
        }
        onSelectConnection={(connection) => setSelectedConnectionId(connection.id)}
        editing
      />

      <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-slate-800 bg-slate-950/45 p-4">
        <p className="text-xs leading-5 text-slate-400">
          Canvas node positions, curve handles, and guild paths save to the
          curriculum draft in Supabase.
        </p>
        <Button
          type="button"
          variant="secondary"
          onClick={() => void saveMapLayout()}
          disabled={savingMapLayout}
        >
          <Icon name="Save" size={16} />
          {savingMapLayout ? "Saving layout..." : "Save map layout"}
        </Button>
      </div>

      <Card className="p-5">
        <div className="grid gap-5 xl:grid-cols-[.95fr_1.05fr]">
          <div>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <p className="font-retro text-[8px] tracking-widest text-cyan-300">
                  GUILD ROUTE
                </p>
                <h2 className="mt-2 font-display text-2xl font-bold text-white">
                  {selectedGuild.shortName} pathway
                </h2>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Pill tone="slate">{guildConnections.length} segments</Pill>
                <Button
                  type="button"
                  variant="danger"
                  disabled={guildConnections.length === 0}
                  onClick={clearGuildPath}
                >
                  <Icon name="X" size={16} />
                  Clear path
                </Button>
              </div>
            </div>

            <div className="mt-5 grid gap-3 lg:grid-cols-[1fr_1fr_auto]">
              <label className="text-xs font-bold text-slate-300">
                From
                <select
                  value={selectedRegionId}
                  onChange={(event) => {
                    setSelectedRegionId(event.target.value);
                    if (event.target.value === routeTargetId) {
                      const nextTarget = draft.regions.find(
                        (region) => region.id !== event.target.value,
                      );
                      if (nextTarget) setRouteTargetId(nextTarget.id);
                    }
                  }}
                  className="mt-2 min-h-11 w-full rounded-xl border border-slate-700 bg-slate-950 px-3 text-sm text-white"
                >
                  {draft.regions.map((region) => (
                    <option key={region.id} value={region.id}>
                      {region.order}. {region.name}
                    </option>
                  ))}
                </select>
              </label>
              <label className="text-xs font-bold text-slate-300">
                To
                <select
                  value={routeTargetId}
                  onChange={(event) => setRouteTargetId(event.target.value)}
                  className="mt-2 min-h-11 w-full rounded-xl border border-slate-700 bg-slate-950 px-3 text-sm text-white"
                >
                  {draft.regions
                    .filter((region) => region.id !== selectedRegionId)
                    .map((region) => (
                      <option key={region.id} value={region.id}>
                        {region.order}. {region.name}
                      </option>
                  ))}
                </select>
              </label>
              <div className="flex flex-wrap gap-2 self-end">
                <Button type="button" onClick={addConnection}>
                  <Icon name={routePairConnection ? "Play" : "Plus"} size={17} />
                  {routePairConnection ? "Select path" : "Add path"}
                </Button>
                <Button
                  type="button"
                  variant="danger"
                  disabled={!routePairConnection}
                  onClick={deleteRoutePair}
                >
                  <Icon name="X" size={16} />
                  Remove path
                </Button>
              </div>
            </div>
          </div>

          <div className="rounded-2xl border border-slate-800 bg-slate-950/45 p-4">
            {selectedConnection ? (
              <div className="space-y-4">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                  <div>
                    <p className="text-xs font-bold text-slate-400">
                      {
                        draft.regions.find(
                          (region) => region.id === selectedConnection.fromRegionId,
                        )?.name
                      }{" "}
                      to{" "}
                      {
                        draft.regions.find(
                          (region) => region.id === selectedConnection.toRegionId,
                        )?.name
                      }
                    </p>
                    <p className="mt-1 text-[11px] text-slate-500">
                      Drag the gold handle on the map or enter exact curve values.
                    </p>
                  </div>
                  <Button
                    type="button"
                    variant="danger"
                    onClick={() => deleteConnection(selectedConnection.id)}
                  >
                    <Icon name="X" size={16} />
                    Delete
                  </Button>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <NumberField
                    label="Curve X %"
                    value={selectedConnection.controlX}
                    step={0.5}
                    onChange={(controlX) =>
                      updateConnection(selectedConnection.id, { controlX })
                    }
                  />
                  <NumberField
                    label="Curve Y %"
                    value={selectedConnection.controlY}
                    step={0.5}
                    onChange={(controlY) =>
                      updateConnection(selectedConnection.id, { controlY })
                    }
                  />
                </div>
              </div>
            ) : (
              <EmptyState
                icon="Navigation"
                title="No route segment selected"
                detail="Add a segment or choose one from the current guild pathway."
              />
            )}
          </div>
        </div>

        {guildConnections.length > 0 && (
          <div className="mt-5 flex gap-2 overflow-x-auto border-t border-slate-800 pt-4">
            {guildConnections.map((connection) => {
              const from = draft.regions.find(
                (region) => region.id === connection.fromRegionId,
              );
              const to = draft.regions.find(
                (region) => region.id === connection.toRegionId,
              );
              return (
                <button
                  key={connection.id}
                  type="button"
                  onClick={() => setSelectedConnectionId(connection.id)}
                  className={`min-w-52 rounded-xl border p-3 text-left text-xs transition ${
                    connection.id === selectedConnection?.id
                      ? "border-amber-300/70 bg-amber-300/10 text-white"
                      : "border-slate-800 bg-slate-950/45 text-slate-500 hover:border-slate-600 hover:text-slate-200"
                  }`}
                >
                  <span className="block truncate font-bold">
                    {from?.name ?? "Unknown"} to {to?.name ?? "Unknown"}
                  </span>
                  <span
                    className="mt-2 block h-1 w-10 rounded-full"
                    style={{ backgroundColor: selectedGuild.color }}
                  />
                </button>
              );
            })}
          </div>
        )}
      </Card>

      <div className="grid gap-6 xl:grid-cols-[.8fr_1.2fr]">
        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-retro text-[8px] tracking-widest text-cyan-300">
                REGION ANCHOR
              </p>
              <h2 className="mt-2 font-display text-2xl font-bold text-white">
                {selectedRegion.name}
              </h2>
            </div>
            <Icon name={selectedRegion.icon} size={26} style={{ color: selectedRegion.color }} />
          </div>
          <div className="mt-6 space-y-4">
            <TextField
              label="Region name"
              value={selectedRegion.name}
              onChange={(name) => updateRegion({ name })}
            />
            <TextField
              label="Topic"
              value={selectedRegion.topic}
              onChange={(topic) => updateRegion({ topic })}
            />
            <TextArea
              label="Description"
              value={selectedRegion.description}
              onChange={(description) => updateRegion({ description })}
            />
            <div className="grid grid-cols-2 gap-3">
              <NumberField
                label="Map X %"
                value={selectedRegion.x}
                onChange={(x) => updateRegion({ x })}
              />
              <NumberField
                label="Map Y %"
                value={selectedRegion.y}
                onChange={(y) => updateRegion({ y })}
              />
              <NumberField
                label="XP available"
                value={selectedRegion.xpAvailable}
                onChange={(xpAvailable) => updateRegion({ xpAvailable })}
              />
              <label className="text-xs font-bold text-slate-300">
                Color
                <input
                  type="color"
                  value={selectedRegion.color}
                  onChange={(event) => updateRegion({ color: event.target.value })}
                  className="mt-2 h-11 w-full rounded-xl border border-slate-700 bg-slate-950 p-1"
                />
              </label>
            </div>
            <div className="rounded-2xl border border-slate-800 bg-slate-950/45 p-4">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div>
                  <p className="font-retro text-[8px] tracking-widest text-amber-300">
                    COHORT UNLOCK
                  </p>
                  <h3 className="mt-2 text-sm font-extrabold text-white">
                    {cohort.name}
                  </h3>
                </div>
                <Pill tone="slate">
                  {(
                    selectedRegion.unlockPrerequisiteRegionIds ??
                    selectedRegion.prerequisiteRegionIds
                  ).length}{" "}
                  prerequisite(s)
                </Pill>
              </div>

              <div className="mt-4 grid gap-3 sm:grid-cols-2">
                <label className="block text-xs font-bold text-slate-300">
                  Release date and time
                  <input
                    type="datetime-local"
                    value={toDateTimeLocalValue(selectedRegion.unlockAvailableFrom)}
                    onChange={(event) =>
                      updateRegionUnlockDraft({
                        unlockAvailableFrom: fromDateTimeLocalValue(
                          event.target.value,
                        ),
                      })
                    }
                    className="mt-2 min-h-11 w-full rounded-xl border border-slate-700 bg-slate-950 px-3 text-sm text-white"
                  />
                </label>
                <NumberField
                  label="Required completion %"
                  value={selectedRegion.unlockCompletionThreshold ?? 70}
                  onChange={(unlockCompletionThreshold) =>
                    updateRegionUnlockDraft({
                      unlockCompletionThreshold: Math.max(
                        0,
                        Math.min(100, unlockCompletionThreshold),
                      ),
                    })
                  }
                />
              </div>

              <div className="mt-4">
                <p className="text-xs font-bold text-slate-300">
                  Completion prerequisites
                </p>
                <div className="mt-2 grid max-h-56 gap-2 overflow-y-auto pr-1 sm:grid-cols-2">
                  {draft.regions
                    .filter((region) => region.id !== selectedRegion.id)
                    .map((region) => {
                      const checked = (
                        selectedRegion.unlockPrerequisiteRegionIds ??
                        selectedRegion.prerequisiteRegionIds
                      ).includes(region.id);
                      return (
                        <label
                          key={region.id}
                          className={`flex min-h-11 cursor-pointer items-center gap-3 rounded-xl border px-3 text-xs font-bold transition ${
                            checked
                              ? "border-amber-300/50 bg-amber-300/10 text-white"
                              : "border-slate-800 bg-slate-950/50 text-slate-500 hover:border-slate-600 hover:text-slate-200"
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleUnlockPrerequisite(region.id)}
                            className="h-4 w-4 accent-amber-300"
                          />
                          <span className="min-w-0 truncate">{region.name}</span>
                        </label>
                      );
                    })}
                </div>
              </div>

              <div className="mt-4 flex flex-wrap gap-2">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() =>
                    updateRegionUnlockDraft({
                      unlockAvailableFrom: new Date().toISOString(),
                    })
                  }
                >
                  <Icon name="Unlock" size={16} />
                  Release now
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() =>
                    updateRegionUnlockDraft({ unlockAvailableFrom: null })
                  }
                >
                  <Icon name="CalendarX" size={16} />
                  Clear date
                </Button>
                <Button
                  type="button"
                  onClick={() => void saveUnlockRules()}
                  disabled={savingUnlocks}
                >
                  <Icon name="Save" size={16} />
                  {savingUnlocks ? "Saving..." : "Save unlocks"}
                </Button>
              </div>
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="font-retro text-[8px] tracking-widest text-violet-300">
                QUEST AUTHORING
              </p>
              <h2 className="mt-2 font-display text-2xl font-bold text-white">
                Template-based activity
              </h2>
            </div>
            <div className="flex flex-wrap gap-2">
              <input
                ref={packageInputRef}
                type="file"
                accept=".json,application/json"
                className="hidden"
                onChange={(event) => void importPuzzlePackage(event)}
              />
              <Button
                type="button"
                variant="secondary"
                onClick={() => packageInputRef.current?.click()}
              >
                <Icon name="Upload" size={17} />
                Import puzzle package
              </Button>
              <Button variant="secondary" onClick={addQuest}>
                <Icon name="Plus" size={17} />
                Add quest
              </Button>
            </div>
          </div>
          <div className="mt-5 flex gap-2 overflow-x-auto pb-2">
            {regionQuests.map((quest) => (
              <button
                key={quest.id}
                onClick={() => setSelectedQuestId(quest.id)}
                className={`min-w-44 rounded-xl border p-3 text-left text-xs transition ${
                  quest.id === selectedQuest?.id
                    ? "border-indigo-400/50 bg-indigo-500/10 text-white"
                    : "border-slate-800 bg-slate-950/45 text-slate-500 hover:border-slate-600"
                }`}
              >
                <span className="line-clamp-2 font-bold">{quest.title}</span>
                <span className="mt-2 block text-[9px] uppercase">
                  {quest.templateId}
                </span>
              </button>
            ))}
          </div>

          {selectedQuest ? (
            <div className="mt-5 space-y-4 border-t border-slate-800 pt-5">
              <div className="grid gap-4 sm:grid-cols-2">
                <TextField
                  label="Quest title"
                  value={selectedQuest.title}
                  onChange={(title) => updateQuest({ title })}
                />
                <label className="text-xs font-bold text-slate-300">
                  Puzzle template
                  <select
                    value={selectedQuest.templateId}
                    onChange={(event) =>
                      updateQuest({
                        templateId: event.target.value as Quest["templateId"],
                      })
                    }
                    className="mt-2 min-h-11 w-full rounded-xl border border-slate-700 bg-slate-950 px-3 text-sm text-white"
                  >
                    {templateOptions.map((option) => (
                      <option key={option.id} value={option.id}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
              <TextArea
                label="Story hook"
                value={selectedQuest.storyHook}
                onChange={(storyHook) => updateQuest({ storyHook })}
              />
              <TextArea
                label="Learning goal"
                value={selectedQuest.learningGoal}
                onChange={(learningGoal) => updateQuest({ learningGoal })}
              />
              <TextArea
                label="Guided practice"
                value={selectedQuest.practice}
                onChange={(practice) => updateQuest({ practice })}
              />
              <TextArea
                label="Reflection prompt"
                value={selectedQuest.reflectionPrompt}
                onChange={(reflectionPrompt) => updateQuest({ reflectionPrompt })}
              />

              <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                <NumberField
                  label="Base XP"
                  value={selectedQuest.xp}
                  onChange={(xp) => updateQuest({ xp })}
                />
                <NumberField
                  label="Grade points"
                  value={selectedQuest.gradePoints}
                  onChange={(gradePoints) => updateQuest({ gradePoints })}
                />
                <NumberField
                  label="Cooldown minutes"
                  value={selectedQuest.cooldownMinutes}
                  onChange={(cooldownMinutes) => updateQuest({ cooldownMinutes })}
                />
                <NumberField
                  label="Max attempts (0 = ∞)"
                  value={selectedQuest.maxAttempts ?? 0}
                  onChange={(value) =>
                    updateQuest({ maxAttempts: value <= 0 ? null : value })
                  }
                />
                <NumberField
                  label="XP decay %"
                  value={selectedQuest.xpDecay * 100}
                  onChange={(value) => updateQuest({ xpDecay: value / 100 })}
                />
                <NumberField
                  label="Grade decay %"
                  value={selectedQuest.gradeDecay * 100}
                  onChange={(value) => updateQuest({ gradeDecay: value / 100 })}
                />
              </div>

              {configFields[selectedQuest.templateId].length > 0 && (
                <div className="rounded-2xl border border-slate-800 bg-slate-950/45 p-4">
                  <p className="mb-4 text-xs font-extrabold uppercase tracking-wider text-slate-400">
                    Puzzle variables
                  </p>
                  {selectedQuest.templateId === "vector-drone" && (
                    <label className="mb-4 block text-xs font-bold text-slate-300">
                      Difficulty progression
                      <select
                        value={String(selectedQuest.config.difficulty ?? "practice")}
                        onChange={(event) =>
                          updateQuest({
                            config: {
                              ...selectedQuest.config,
                              difficulty: event.target.value as VectorDroneDifficulty,
                            },
                          })
                        }
                        className="mt-2 min-h-11 w-full rounded-xl border border-slate-700 bg-slate-950 px-3 text-sm text-white"
                      >
                        {Object.entries(vectorDroneDifficultyLabels).map(
                          ([id, label]) => (
                            <option key={id} value={id}>
                              {label}
                            </option>
                          ),
                        )}
                      </select>
                    </label>
                  )}
                  <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
                    {configFields[selectedQuest.templateId].map((field) => (
                      <NumberField
                        key={field.key}
                        label={field.label}
                        step={field.step}
                        value={Number(selectedQuest.config[field.key] ?? 0)}
                        onChange={(value) =>
                          updateQuest({
                            config: {
                              ...selectedQuest.config,
                              [field.key]: value,
                            },
                          })
                        }
                      />
                    ))}
                  </div>
                </div>
              )}

              <div className="flex flex-wrap gap-3">
                <Button onClick={() => void saveDraft()}>
                  <Icon name="Save" size={17} />
                  Save quest draft
                </Button>
                <Button
                  variant="secondary"
                  disabled={selectedQuest.templateId === "coming-soon"}
                  onClick={() => setPreview((current) => !current)}
                >
                  <Icon name="Play" size={17} />
                  {preview ? "Hide preview" : "Preview puzzle"}
                </Button>
              </div>
            </div>
          ) : (
            <EmptyState
              icon="ScrollText"
              title="No quest selected"
              detail="Choose a quest above or create a new activity from a registered template."
            />
          )}
        </Card>
      </div>

      {preview && selectedQuest && previewInstance !== null && (
        <div className="space-y-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2 className="font-display text-2xl font-bold text-white">
                Faculty preview
              </h2>
              <p className="mt-1 text-xs text-slate-500">
                Seed {previewSeed}
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                variant="secondary"
                onClick={() => setPreviewSeed(createTimeSeed())}
              >
                <Icon name="WandSparkles" size={16} />
                Randomize puzzle
              </Button>
              {previewResult && (
                <Pill tone={previewResult.success ? "emerald" : "amber"}>
                  {previewResult.success ? "Correct" : "Not correct"} -{" "}
                  {previewResult.score}
                </Pill>
              )}
            </div>
          </div>
          <PuzzlePlayer
            quest={selectedQuest}
            instance={previewInstance}
            onLock={(submission) => {
              const validation = validatePuzzleSubmission(
                selectedQuest.templateId,
                selectedQuest.config,
                previewInstance,
                submission,
              );
              setPreviewResult(validation);
              notify(
                validation.success ? "Preview correct" : "Preview needs work",
                validation.feedback,
                validation.success ? "success" : "warning",
              );
            }}
          />
          {previewResult && (
            <Card
              className={`p-4 ${
                previewResult.success
                  ? "border border-emerald-300/30"
                  : "border border-amber-300/30"
              }`}
            >
              <p className="text-sm font-bold text-white">
                {previewResult.success ? "Correct" : "Not correct"} - Score{" "}
                {previewResult.score}
              </p>
              <p className="mt-2 text-sm leading-6 text-slate-300">
                {previewResult.feedback}
              </p>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}

function createQuestFromPackage(
  puzzlePackage: PuzzlePackageDefinition,
  region: Region,
): Quest {
  const subArea = region.subAreas[0];
  const competencyIds =
    puzzlePackage.reflection.mode === "quiz"
      ? [
          ...new Set(
            puzzlePackage.reflection.questions.flatMap(
              (question) => question.competencyIds,
            ),
          ),
        ]
      : [];
  const evidenceIds =
    competencyIds.length > 0
      ? competencyIds
      : subArea
        ? [`${subArea.id}-competency`]
        : [];
  const reflectionPrompt =
    puzzlePackage.reflection.mode === "text"
      ? puzzlePackage.reflection.prompt
      : [
          puzzlePackage.reflection.title,
          puzzlePackage.reflection.instructions,
        ]
          .filter(Boolean)
          .join(" - ");

  return {
    id: `${puzzlePackage.packageId}-${crypto.randomUUID()}`,
    regionId: region.id,
    subAreaId: subArea?.id ?? region.id,
    title: puzzlePackage.title,
    storyHook: puzzlePackage.questDefaults.storyHook,
    learningGoal: puzzlePackage.questDefaults.learningGoal,
    practice: puzzlePackage.questDefaults.practice,
    reflectionPrompt,
    reflectionConfig: puzzlePackage.reflection,
    templateId: puzzlePackage.templateId,
    templateVersion: puzzlePackage.templateVersion,
    config: puzzlePackage.config,
    xp: puzzlePackage.questDefaults.xp,
    gradePoints: puzzlePackage.questDefaults.gradePoints,
    xpDecay: 0.2,
    gradeDecay: 0.1,
    cooldownMinutes: puzzlePackage.questDefaults.cooldownMinutes,
    maxAttempts: puzzlePackage.questDefaults.maxAttempts,
    competencyEvidence: Object.fromEntries(
      evidenceIds.map((competencyId) => [competencyId, 25]),
    ),
    boss: false,
    status: "draft",
    version: 1,
  };
}

function toDateTimeLocalValue(value?: string | null) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60_000);
  return local.toISOString().slice(0, 16);
}

function fromDateTimeLocalValue(value: string) {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
}

function TextField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange(value: string): void;
}) {
  return (
    <label className="block text-xs font-bold text-slate-300">
      {label}
      <input
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="mt-2 min-h-11 w-full rounded-xl border border-slate-700 bg-slate-950 px-3 text-sm text-white"
      />
    </label>
  );
}

function TextArea({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange(value: string): void;
}) {
  return (
    <label className="block text-xs font-bold text-slate-300">
      {label}
      <textarea
        rows={3}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="mt-2 w-full rounded-xl border border-slate-700 bg-slate-950 p-3 text-sm leading-6 text-white"
      />
    </label>
  );
}

function NumberField({
  label,
  value,
  step = 1,
  onChange,
}: {
  label: string;
  value: number;
  step?: number;
  onChange(value: number): void;
}) {
  return (
    <label className="block text-xs font-bold text-slate-300">
      {label}
      <input
        type="number"
        step={step}
        value={value}
        onChange={(event) => onChange(Number(event.target.value))}
        className="mt-2 min-h-11 w-full rounded-xl border border-slate-700 bg-slate-950 px-3 text-sm text-white"
      />
    </label>
  );
}
